import React from 'react';
import { Sparkles, RotateCcw } from 'lucide-react';

const DEFAULT_PROMPT = `Sən Karma Matrisi üzrə təcrübəli koç və mentorsan. Azerbaycan dilində cavab verirsən.

İstifadəçinin doğum tarixini və karma matrisindən hesablanmış göstəriciləri aldığında, onlara əsasən **qısa (3-4 abzas), sadə, müasir və motivasiyaedici koç/mentor tərzində** şəxsi oxunuş hazırla.

Göstəricilər:
- A, A1 – doğum günü və əlavə dəyəri
- B, B1 – doğum ayı
- V, V1 – il cəmi  
- G, G1 – ümumi yekun (həyat yolu)
- D, E, Zh, Z – Nailiyyət ədədləri (NƏ1-4)
- I, K, L, M – Karmik düyünlər (KD1-4)
- HRT – Ürək (fiziki/emosional mərkəz)
- Man – Manipura (enerji mərkəzi)
- Gates (Qapılar) – 4 dövr: 0-30, 30-39, 39-48, 48+ yaş

Struktur:
1. Qısa giriş (1 cümlə doğum tarixinə istinadla)
2. Güclü tərəflər və potensial (1-2 abzas, konkret göstəricilərə istinad et)
3. Karmik çağırışlar və qapı dövrləri üçün qısa məsləhət (1 abzas)
4. Bir motivasiyaedici yekun cümlə

**Stil qaydaları:**
- Sadə, müasir Azərbaycan dili (arxaik sözlər yox)  
- "Sən" formasında birbaşa müraciət
- Konkret, praktiki məsləhətlər
- Qətiyyətlə gələcəyi proqnozlaşdırma; potensial və yol kimi təqdim et
- Emoji istifadə etmə`;

const MODEL_OPTIONS = {
  openai: ['gpt-5.2', 'gpt-5.1', 'gpt-5', 'gpt-5-mini', 'gpt-4o', 'gpt-4.1'],
  anthropic: ['claude-sonnet-4-5-20250929', 'claude-opus-4-5-20251101', 'claude-haiku-4-5-20251001'],
  // GA / stable models first so default selection works on free tier.
  // gemini-3.x models often need elevated quota; keep them at the bottom.
  gemini: [
    'gemini-2.5-flash',
    'gemini-2.5-pro',
    'gemini-2.5-flash-lite',
    'gemini-2.0-flash',
    'gemini-2.0-flash-lite',
    'gemini-3.1-flash-lite',
    'gemini-3.1-pro-preview',
  ],
};

const AISettingsEditor = ({
  aiEnabled,
  aiProvider,
  aiModel,
  aiSystemPrompt,
  aiCustomApiKeySet,
  aiCustomApiKeyPreview,
  onChange,
  onSaveImmediate,
}) => {
  const provider = aiProvider || 'openai';
  const model = aiModel || 'gpt-5.2';
  const prompt = aiSystemPrompt ?? DEFAULT_PROMPT;
  const models = MODEL_OPTIONS[provider] || ['gpt-5.2'];
  const [keyDraft, setKeyDraft] = React.useState('');
  const [showKey, setShowKey] = React.useState(false);
  const [keyBusy, setKeyBusy] = React.useState(false);

  const saveKey = async (value) => {
    if (!onSaveImmediate) {
      onChange({ ai_custom_api_key: value });
      return;
    }
    setKeyBusy(true);
    try {
      await onSaveImmediate({ ai_custom_api_key: value });
      setKeyDraft('');
    } catch {
      // error already shown by parent
    } finally {
      setKeyBusy(false);
    }
  };

  return (
    <div className="admin-section">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles size={18} className="text-yellow-300" />
        <h3 className="admin-section-title" style={{ margin: 0 }}>AI Koç Şərhi</h3>
      </div>
      <p className="admin-section-desc">
        Hər hesablamadan sonra avtomatik olaraq AI ilə şəxsi şərh yaradılır. Burada modelı, provayderi və sistem prompt-unu konfiqurasiya edə bilərsiniz.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={!!aiEnabled}
            onChange={(e) => onChange({ ai_enabled: e.target.checked })}
            data-testid="ai-enabled-toggle"
          />
          <span className="text-sm text-white/80">AI şərhi aktivdir (ana səhifədə avtomatik göstərilir)</span>
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-white/60">Provayder</label>
          <select
            className="karma-input"
            value={provider}
            onChange={(e) => {
              const newProvider = e.target.value;
              const newModels = MODEL_OPTIONS[newProvider] || ['gpt-5.2'];
              onChange({ ai_provider: newProvider, ai_model: newModels[0] });
            }}
            data-testid="ai-provider-select"
          >
            <option value="openai">OpenAI</option>
            <option value="anthropic">Anthropic (Claude)</option>
            <option value="gemini">Google Gemini</option>
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-white/60">Model</label>
          <select
            className="karma-input"
            value={model}
            onChange={(e) => onChange({ ai_model: e.target.value })}
            data-testid="ai-model-select"
          >
            {models.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
      </div>

      {/* Custom API Key */}
      <div className="flex flex-col gap-2 mb-4 p-3 rounded border border-yellow-500/20 bg-yellow-500/5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <label className="text-sm font-semibold text-yellow-200">
            Şəxsi API Açarı (Custom API Key)
          </label>
          <span className="text-xs text-white/50">
            {aiCustomApiKeySet
              ? `Aktiv: ${aiCustomApiKeyPreview || '***'}`
              : 'Boş — Universal Key istifadə olunur'}
          </span>
        </div>
        <p className="text-xs text-white/60 leading-relaxed">
          Boş buraxılarsa, Emergent Universal Key istifadə olunur. Öz hesabınızla qoşulmaq üçün yuxarıdakı provayderdən aldığınız API açarını buraya yazın:
          <br />• <strong>OpenAI</strong>: <a href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer" className="text-yellow-300 underline">platform.openai.com/api-keys</a>
          <br />• <strong>Google Gemini</strong>: <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noreferrer" className="text-yellow-300 underline">aistudio.google.com/app/apikey</a>
          <br />• <strong>Anthropic Claude</strong>: <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noreferrer" className="text-yellow-300 underline">console.anthropic.com/settings/keys</a>
        </p>
        <div className="flex gap-2 flex-wrap">
          <input
            type={showKey ? 'text' : 'password'}
            className="karma-input flex-1 text-left"
            style={{ minWidth: 200, fontFamily: 'monospace', fontSize: 12 }}
            placeholder={aiCustomApiKeySet ? 'Yenisini yazın və ya boş buraxın' : 'sk-... və ya AIza...'}
            value={keyDraft}
            onChange={(e) => setKeyDraft(e.target.value)}
            data-testid="ai-custom-key-input"
          />
          <button
            type="button"
            className="admin-btn-secondary"
            onClick={() => setShowKey(!showKey)}
            title={showKey ? 'Gizlət' : 'Göstər'}
          >
            {showKey ? 'Gizlət' : 'Göstər'}
          </button>
          <button
            type="button"
            className="admin-btn-primary"
            onClick={() => saveKey(keyDraft)}
            disabled={!keyDraft || keyBusy}
            data-testid="ai-custom-key-save"
          >
            {keyBusy ? 'Saxlanır...' : 'Yadda saxla'}
          </button>
          {aiCustomApiKeySet && (
            <button
              type="button"
              className="admin-btn-secondary"
              style={{ borderColor: '#ef4444', color: '#fca5a5' }}
              onClick={() => saveKey('')}
              disabled={keyBusy}
              data-testid="ai-custom-key-clear"
            >
              {keyBusy ? '...' : 'Açarı sil'}
            </button>
          )}
        </div>
        <p className="text-xs text-white/40">
          ⚠️ Açar yalnız admin endpoint-də saxlanır və geri oxunmur (yalnız önizləmə göstərilir).
          "Yadda saxla" və "Açarı sil" düymələri dəyişikliyi dərhal tətbiq edir — əlavə "Saxla" basmağa ehtiyac yoxdur.
        </p>
      </div>

      <div className="flex flex-col gap-2 mb-2">
        <div className="flex items-center justify-between">
          <label className="text-xs text-white/60">Sistem prompt (AI-a verilən təlimat)</label>
          <button
            type="button"
            onClick={() => onChange({ ai_system_prompt: DEFAULT_PROMPT })}
            className="admin-btn-secondary text-xs"
            title="Defolt prompta qayıt"
            data-testid="ai-prompt-reset"
          >
            <RotateCcw size={12} /> Defolt
          </button>
        </div>
        <textarea
          className="karma-input"
          style={{ width: '100%', minHeight: 280, fontFamily: "'Inter', sans-serif", fontSize: 13, lineHeight: 1.6, maxWidth: '100%' }}
          value={prompt}
          onChange={(e) => onChange({ ai_system_prompt: e.target.value })}
          data-testid="ai-system-prompt"
          placeholder="AI-a verəcəyiniz sistem təlimatını buraya yazın..."
        />
        <p className="text-xs text-white/40">
          Limit: 8000 simvol. AI bu təlimata əməl edərək doğum tarixinə və göstəricilərə uyğun cavab verəcək.
        </p>
      </div>
    </div>
  );
};

export default AISettingsEditor;
